/**
 * Campaign Mission resume panel — Milestone 37.2.
 *
 * Surfaces the founder's ONE active Campaign mission on Executive Home.
 * When multiple Campaign missions are resumable, we render the most
 * recently updated one — matching the Focus Flow doctrine that the
 * founder should never be forced to triage.
 */
import { useNavigate } from "react-router-dom";

import { useResumableMissions, type Mission } from "@/entities/missions";
import { MissionResumeTile } from "@/features/focus-flow";
import { useCurrentWorkspace } from "@/entities/workspace";

const STAGE_COPY: Record<string, string> = {
  plan:     "Reviewing your strategy",
  generate: "Generating your assets",
  review:   "Reviewing generated assets",
  approve:  "Approving your assets",
  vault:    "Moving to the Vault",
  schedule: "Scheduling your campaign",
  complete: "Your campaign is ready",
};

export function CampaignResumePanel(): JSX.Element | null {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;
  const { data } = useResumableMissions(workspaceId);
  const navigate = useNavigate();

  const campaigns = (data?.items ?? []).filter(
    (m: Mission) =>
      m.mission_type === "campaign" &&
      (m.status === "active" || m.status === "paused"),
  );
  if (campaigns.length === 0) return null;

  // Deterministic — server already sorts by ``updated_at DESC``.
  const mission = campaigns[0];
  const marker = (mission.memory.data.lifecycle_marker as string) || "plan";
  const stageDetail = STAGE_COPY[marker] ?? "Continue campaign";
  const displayName =
    (mission.memory.data.campaign_name as string) || "Untitled campaign";
  const resumeRoute = (mission.memory.data.resume_route as string) || "/campaigns";

  return (
    <section
      data-testid="obsidian-section-campaign-resume"
      className="obsidian-card p-5 md:p-6"
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-300/80 mb-3">
        Continue your campaign
      </div>
      <MissionResumeTile
        mission={mission}
        displayName={displayName}
        stageDetail={stageDetail}
        onResume={() => navigate(resumeRoute)}
        testIdRoot="campaign-resume-tile"
      />
    </section>
  );
}
